import React, { useState, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useServices } from '../context/ServiceContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import './Leads.css';

/* ── Constants ── */
const SOURCES = ['All','Meta Ads','BNI Referral','Website','Referral','Google Ads','Instagram','LinkedIn','Cold Call','Direct','Event','Other'];
const MEMBERS = ['Lakshmi','Arjun','Meera','Kiran','Shafeeq'];
const COLUMNS = [
  {id:'new',        label:'New',            color:'#60A5FA'},
  {id:'contacted',  label:'Contacted',      color:'#3B82F6'},
  {id:'meeting',    label:'Meeting Done',   color:'#A78BFA'},
  {id:'proposal',   label:'Proposal Sent',  color:'#F59E0B'},
  {id:'negotiation',label:'Negotiation',    color:'#F5A623'},
  {id:'won',        label:'Won',            color:'#22C55E'},
  {id:'lost',       label:'Lost',           color:'#EF4444'},
];
const STATUS_OPTIONS = ['','Unpaid','Partially Paid','Paid','Overdue','Cancelled'];

const QUALITY_MAP = {
  'hot lead':'negotiation','hot':'negotiation','warm':'contacted','cold':'new',
  'contacted':'contacted','converted':'won','lost':'lost','closed':'won','new':'new',
  'meeting':'meeting','proposal':'proposal','negotiation':'negotiation','won':'won',
};
const mapQuality = q => q ? (QUALITY_MAP[q.toLowerCase().trim()] || 'new') : 'new';

const INIT = [];

/* ── Helpers ── */
const today = () => new Date().toISOString().split('T')[0];
const isOverdue = d => d && d < today();
const initials = n => n.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const fmtVal = v => v>=100000?'₹'+(v/100000).toFixed(1)+'L':v>=1000?'₹'+(v/1000).toFixed(0)+'K':'₹'+v;
const fmtDate = d => d?new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short'}):'—';
const genId = () => Date.now() + Math.random();

/* ── Icons ── */
const KanbanIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="5" height="18" rx="1"/><rect x="10" y="3" width="5" height="12" rx="1"/><rect x="17" y="3" width="5" height="15" rx="1"/></svg>;
const ListIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3" cy="6" r="1.5" fill="currentColor"/><circle cx="3" cy="12" r="1.5" fill="currentColor"/><circle cx="3" cy="18" r="1.5" fill="currentColor"/></svg>;
const CloseIcon  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const SearchIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const UploadIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>;

/* ── Status badge ── */
function Badge({status}) {
  const map={new:'ld-b-green',contacted:'ld-b-blue',warm:'ld-b-sky',hot:'ld-b-gold',closed:'ld-b-green',lost:'ld-b-red'};
  return <span className={`ld-badge ${map[status]||''}`}>{status==='closed'?'Closed Won':status.charAt(0).toUpperCase()+status.slice(1)}</span>;
}

/* ── Convert to Client confirm ── */
function ConvertModal({lead,theme,onConfirm,onClose}) {
  return (
    <div className="ld-overlay" onClick={onClose}>
      <div className="ld-modal" data-theme={theme} style={{width:400}} onClick={e=>e.stopPropagation()}>
        <div className="ld-modal-hd"><span className="ld-modal-title">Convert to Client</span><button className="ld-icon-btn" onClick={onClose}><CloseIcon/></button></div>
        <div className="ld-modal-body">
          <p style={{fontSize:13,lineHeight:1.6,color: theme==='dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'}}>Move <strong style={{color: theme==='dark'?'#fff':'#0f0c08'}}>{lead.name}</strong> from Leads to Clients. They will no longer appear in the lead pipeline.</p>
          <div style={{display:'flex',gap:8,marginTop:14}}>
            <button className="ld-btn-pri" style={{flex:1}} onClick={onConfirm}>Confirm Convert</button>
            <button className="ld-btn-sec" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Add / Edit Lead Modal ── */
function LeadModal({lead,theme,onClose,onSave}) {
  const {services} = useServices();
  const blank = {name:'',company:'',businessType:'',phone:'',email:'',location:'',source:'Meta Ads',contactMethod:'WhatsApp',status:'new',payStatus:'',assigned:'Lakshmi',value:'',followUp:'',service:'',notes:'',followUps:['','','','','']};
  const [form,setForm] = useState(lead ? {
    ...lead,
    followUps: [...(lead.followUps||[]),...Array(5)].slice(0,5),
  } : blank);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const setFU = (i,v) => setForm(f=>{const fu=[...f.followUps];fu[i]=v;return{...f,followUps:fu};});

  const submit = () => {
    if (!form.name.trim()) return;
    onSave({...form,id:lead?lead.id:genId(),value:parseInt(form.value)||0,followUps:form.followUps.filter(Boolean)});
    onClose();
  };

  return (
    <div className="ld-overlay" onClick={onClose}>
      <div className="ld-modal" data-theme={theme} onClick={e=>e.stopPropagation()}>
        <div className="ld-modal-hd">
          <span className="ld-modal-title">{lead?'Edit Lead':'Add Lead'}</span>
          <button className="ld-icon-btn" onClick={onClose}><CloseIcon/></button>
        </div>
        <div className="ld-modal-body">
          <div className="ld-form-row">
            <div className="ld-form-group"><label className="ld-lbl">Name *</label><input className="ld-inp" placeholder="Full name" value={form.name} onChange={e=>set('name',e.target.value)}/></div>
            <div className="ld-form-group"><label className="ld-lbl">Company</label><input className="ld-inp" placeholder="Company name" value={form.company} onChange={e=>set('company',e.target.value)}/></div>
          </div>
          <div className="ld-form-row">
            <div className="ld-form-group"><label className="ld-lbl">Phone</label><input className="ld-inp" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={e=>set('phone',e.target.value)}/></div>
            <div className="ld-form-group"><label className="ld-lbl">Email</label><input className="ld-inp" placeholder="email@company.com" value={form.email} onChange={e=>set('email',e.target.value)}/></div>
          </div>
          <div className="ld-form-row">
            <div className="ld-form-group"><label className="ld-lbl">Business Type</label><input className="ld-inp" placeholder="e.g. Restaurant, IT, Retail" value={form.businessType} onChange={e=>set('businessType',e.target.value)}/></div>
            <div className="ld-form-group"><label className="ld-lbl">Location</label><input className="ld-inp" placeholder="City, State" value={form.location} onChange={e=>set('location',e.target.value)}/></div>
          </div>
          <div className="ld-form-row">
            <div className="ld-form-group"><label className="ld-lbl">Source</label>
              <select className="ld-inp ld-sel" value={form.source} onChange={e=>set('source',e.target.value)}>{SOURCES.filter(s=>s!=='All').map(s=><option key={s}>{s}</option>)}</select>
            </div>
            <div className="ld-form-group"><label className="ld-lbl">Contact via</label>
              <select className="ld-inp ld-sel" value={form.contactMethod} onChange={e=>set('contactMethod',e.target.value)}>{['WhatsApp','Call','Email','In-Person'].map(s=><option key={s}>{s}</option>)}</select>
            </div>
          </div>
          <div className="ld-form-row">
            <div className="ld-form-group"><label className="ld-lbl">Stage</label>
              <select className="ld-inp ld-sel" value={form.status} onChange={e=>set('status',e.target.value)}>{COLUMNS.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}</select>
            </div>
            <div className="ld-form-group"><label className="ld-lbl">Payment Status</label>
              <select className="ld-inp ld-sel" value={form.payStatus} onChange={e=>set('payStatus',e.target.value)}>{STATUS_OPTIONS.map(s=><option key={s} value={s}>{s||'—'}</option>)}</select>
            </div>
          </div>
          <div className="ld-form-row">
            <div className="ld-form-group"><label className="ld-lbl">Service</label>
              <select className="ld-inp ld-sel" value={form.service} onChange={e=>set('service',e.target.value)}>
                <option value="">Select service</option>
                {(services||['Web Development','Mobile App','CRM','SEO','AMC','WhatsApp Automation']).map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="ld-form-group"><label className="ld-lbl">Budget (₹)</label><input className="ld-inp" type="number" placeholder="e.g. 50000" value={form.value} onChange={e=>set('value',e.target.value)}/></div>
          </div>
          <div className="ld-form-row">
            <div className="ld-form-group"><label className="ld-lbl">Assigned to</label>
              <select className="ld-inp ld-sel" value={form.assigned} onChange={e=>set('assigned',e.target.value)}>{MEMBERS.map(m=><option key={m}>{m}</option>)}</select>
            </div>
            <div className="ld-form-group"><label className="ld-lbl">Follow-up date</label><input className="ld-inp" type="date" value={form.followUp} onChange={e=>set('followUp',e.target.value)}/></div>
          </div>
          {/* Follow-up notes */}
          <div className="ld-form-group"><label className="ld-lbl">Follow-up log</label>
            {[0,1,2,3,4].map(i=>(
              <input key={i} className="ld-inp" style={{marginBottom:4}} placeholder={`Follow-up ${i+1}`} value={form.followUps[i]||''} onChange={e=>setFU(i,e.target.value)}/>
            ))}
          </div>
          <div className="ld-form-group"><label className="ld-lbl">Notes</label><textarea className="ld-inp ld-ta" rows="2" placeholder="Any additional notes..." value={form.notes} onChange={e=>set('notes',e.target.value)}/></div>
        </div>
        <div className="ld-modal-ft">
          <button className="ld-btn-sec" onClick={onClose}>Cancel</button>
          <button className="ld-btn-pri" onClick={submit}>{lead?'Save Changes':'Add Lead'}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Import Modal ── */
function ImportModal({theme,onClose,onImport}) {
  const fileRef = useRef();
  const [status,setStatus] = useState('');
  const [preview,setPreview] = useState([]);

  const parseCSV = text => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h=>h.trim().replace(/^"|"$/g,'').toLowerCase().replace(/\s+/g,'_'));
    return lines.slice(1).map(line=>{
      const vals = line.match(/(".*?"|[^,]+|(?<=,)(?=,)|(?<=,)$|^(?=,))/g)||line.split(',');
      const obj = {};
      headers.forEach((h,i)=>{ obj[h]=(vals[i]||'').replace(/^"|"$/g,'').trim(); });
      return obj;
    }).filter(r=>r.full_name||r.name);
  };

  const rowToLead = row => ({
    id: genId(),
    name: row.full_name || row.name || '',
    company: row.company_name || row.company || '',
    businessType: row.business_type || '',
    phone: row.phone_number || row.phone || '',
    email: row.email || '',
    location: row.location || '',
    source: row.lead_type || row.source || 'Other',
    contactMethod: row['call/whatsapp'] || row.contact_method || 'WhatsApp',
    status: mapQuality(row.lead_quality || row.status || ''),
    payStatus: row.status || '',
    assigned: row.assigned || 'Lakshmi',
    value: parseInt((row.budget||'').replace(/[^0-9]/g,''))||0,
    followUp: '',
    service: row.service_type || row.service || 'Web Development',
    notes: [row.notes_1, row.notes_2].filter(Boolean).join(' | '),
    followUps: [row.follow_up_1,row.follow_up_2,row.follow_up_3,row.follow_up_4,row.follow_up_5].filter(Boolean),
  });

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const rows = parseCSV(ev.target.result);
        const leads = rows.map(rowToLead).filter(l=>l.name);
        setPreview(leads.slice(0,5));
        setStatus(`Found ${leads.length} leads. Preview shows first 5.`);
        if (leads.length > 0) {
          setTimeout(()=>{ onImport(leads); onClose(); },1500);
        }
      } catch(err) { setStatus('Could not parse file. Make sure it is a CSV with column headers.'); }
    };
    reader.readAsText(file);
  };

  return (
    <div className="ld-overlay" onClick={onClose}>
      <div className="ld-modal" data-theme={theme} style={{width:480}} onClick={e=>e.stopPropagation()}>
        <div className="ld-modal-hd"><span className="ld-modal-title">Import Leads from Sheet</span><button className="ld-icon-btn" onClick={onClose}><CloseIcon/></button></div>
        <div className="ld-modal-body">
          <div className="ld-import-hint">
            Export your Google Sheet as CSV (File → Download → CSV). Required columns from your Lead Tracker: Full Name, Phone Number, Lead Type, Business Type, Lead Quality, Budget, Status, Follow Up 1-5, Notes 1-2.
          </div>
          <div className="ld-import-zone" onClick={()=>fileRef.current.click()}>
            <UploadIcon/>
            <div className="ld-import-label">Click to choose CSV file</div>
            <div className="ld-import-sub">Exported from Google Sheets</div>
            <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={handleFile}/>
          </div>
          {status && <div className="ld-import-status">{status}</div>}
          {preview.length>0 && (
            <div>
              <div className="ld-lbl" style={{marginBottom:6}}>Preview</div>
              {preview.map((l,i)=>(
                <div key={i} className="ld-import-row">
                  <Badge status={l.status}/>
                  <span className="ld-import-name">{l.name}</span>
                  <span className="ld-import-src">{l.source}</span>
                  {l.value>0 && <span className="ld-import-val">{fmtVal(l.value)}</span>}
                </div>
              ))}
            </div>
          )}
          <div className="ld-merge-note">Merge mode: matches on phone number. New leads are added, existing ones are updated.</div>
        </div>
      </div>
    </div>
  );
}

/* ── Lead detail panel ── */
function LeadPanel({lead,theme,onClose,onEdit,onDelete,onConvert}) {
  const col = COLUMNS.find(c=>c.id===lead.status);
  return (
    <div className="ld-panel-wrap">
      <div className="ld-panel-overlay" onClick={onClose}/>
      <div className="ld-panel" data-theme={theme}>
        <div className="ld-panel-hd">
          <div className="ld-panel-av">{initials(lead.name)}</div>
          <div className="ld-panel-info">
            <div className="ld-panel-name">{lead.name}</div>
            <div className="ld-panel-co">{lead.company||lead.businessType||'—'}</div>
          </div>
          <button className="ld-icon-btn" onClick={onClose}><CloseIcon/></button>
        </div>
        <div className="ld-panel-actions">
          <Badge status={lead.status}/>
          {lead.payStatus && <span className="ld-pay-badge">{lead.payStatus}</span>}
          {lead.phone && <a className="ld-act-btn" href={`tel:${lead.phone}`}>Call</a>}
          {lead.phone && <a className="ld-act-btn ld-act-wa" href={`https://wa.me/${lead.phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer">WhatsApp</a>}
        </div>
        <div className="ld-panel-body">
          <div className="ld-panel-section">
            <div className="ld-panel-slbl">Contact</div>
            <div className="ld-kv-grid">
              <span className="ld-k">Phone</span><span className="ld-v">{lead.phone||'—'}</span>
              <span className="ld-k">Email</span><span className="ld-v">{lead.email||'—'}</span>
              <span className="ld-k">Location</span><span className="ld-v">{lead.location||'—'}</span>
              <span className="ld-k">Via</span><span className="ld-v">{lead.contactMethod||'—'}</span>
            </div>
          </div>
          <div className="ld-panel-section">
            <div className="ld-panel-slbl">Lead Info</div>
            <div className="ld-kv-grid">
              <span className="ld-k">Source</span><span className="ld-v">{lead.source}</span>
              <span className="ld-k">Service</span><span className="ld-v">{lead.service||'—'}</span>
              <span className="ld-k">Budget</span><span className="ld-v ld-v-gold">{lead.value?fmtVal(lead.value):'—'}</span>
              <span className="ld-k">Business</span><span className="ld-v">{lead.businessType||'—'}</span>
              <span className="ld-k">Assigned</span><span className="ld-v">{lead.assigned}</span>
              <span className="ld-k">Follow-up</span><span className={`ld-v ${isOverdue(lead.followUp)?'ld-v-red':''}`}>{fmtDate(lead.followUp)}</span>
            </div>
          </div>
          {lead.followUps?.length>0 && (
            <div className="ld-panel-section">
              <div className="ld-panel-slbl">Follow-up log</div>
              {lead.followUps.map((f,i)=><div key={i} className="ld-fu-item">{i+1}. {f}</div>)}
            </div>
          )}
          {lead.notes && (
            <div className="ld-panel-section">
              <div className="ld-panel-slbl">Notes</div>
              <div className="ld-panel-notes">{lead.notes}</div>
            </div>
          )}
        </div>
        <div className="ld-panel-ft">
          <button className="ld-btn-pri" style={{flex:1}} onClick={()=>onEdit(lead)}>Edit</button>
          {(lead.status==='won'||lead.status==='closed') && <button className="ld-btn-convert" onClick={()=>onConvert(lead)}>→ Convert to Client</button>}
          <button className="ld-btn-danger" onClick={()=>{onDelete(lead.id);onClose();}}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Kanban Card ── */
function LeadCard({lead,onDragStart,onClick}) {
  const over = isOverdue(lead.followUp);
  return (
    <div className={`ld-card${over?' ld-card-overdue':''}`} draggable onDragStart={()=>onDragStart(lead)} onClick={()=>onClick(lead)}>
      <div className="ld-card-top">
        <div className="ld-card-av">{initials(lead.name)}</div>
        <div><div className="ld-card-name">{lead.name}</div><div className="ld-card-co">{lead.company||lead.businessType||''}</div></div>
      </div>
      <div className="ld-card-meta">
        <span className="ld-card-src">{lead.source}</span>
        {lead.value>0 && <span className="ld-card-val">{fmtVal(lead.value)}</span>}
      </div>
      {lead.payStatus && <div className="ld-card-pay">{lead.payStatus}</div>}
      <div className="ld-card-bottom">
        {lead.followUp && <span className={`ld-card-fu${over?' ld-fu-over':''}`}>{over?'Overdue':fmtDate(lead.followUp)}</span>}
        <span className="ld-card-assignee">{lead.assigned}</span>
      </div>
    </div>
  );
}

/* ── Main ── */
export default function Leads() {
  const {theme} = useTheme();
  const [leads,setLeads] = useState(() => {
    try {
      const saved = localStorage.getItem('ryzenos_leads');
      return saved ? JSON.parse(saved) : INIT;
    } catch { return INIT; }
  });

  // Persist to localStorage on every change
  React.useEffect(() => {
    try { localStorage.setItem('ryzenos_leads', JSON.stringify(leads)); } catch {}
  }, [leads]);
  const [view,setView] = useState('kanban');
  const [search,setSearch] = useState('');
  const [filterSrc,setFilterSrc] = useState('All');
  const [addModal,setAddModal] = useState(false);
  const [editLead,setEditLead] = useState(null);
  const [panelLead,setPanelLead] = useState(null);
  const [importModal,setImportModal] = useState(false);
  const [convertLead,setConvertLead] = useState(null);
  const [clients,setClients] = useState([]);
  const dragRef = useRef(null);

  const filtered = leads.filter(l=>{
    const q = search.toLowerCase();
    const matchQ = !q || l.name.toLowerCase().includes(q)||l.company.toLowerCase().includes(q)||l.phone.includes(q);
    const matchS = filterSrc==='All'||l.source===filterSrc;
    return matchQ && matchS;
  });

  const colLeads = col => filtered.filter(l=>l.status===col.id);

  /* Strip totals */
  const strip = COLUMNS.map(c=>({
    ...c,
    count: leads.filter(l=>l.status===c.id).length,
    total: leads.filter(l=>l.status===c.id).reduce((s,l)=>s+l.value,0),
  }));

  /* Drag-and-drop */
  const onDragStart = (lead) => { dragRef.current = lead; };
  const onDrop = (e, colId) => {
    e.preventDefault();
    if (!dragRef.current) return;
    const dragged = dragRef.current;
    dragRef.current = null;
    setLeads(prev => prev.map(l => l.id === dragged.id ? {...l, status: colId} : l));
  };

  /* CRUD */
  const saveLead = lead => setLeads(prev=>{
    const exists = prev.find(l=>l.id===lead.id);
    return exists ? prev.map(l=>l.id===lead.id?lead:l) : [...prev,lead];
  });

  const deleteLead = id => setLeads(prev=>prev.filter(l=>l.id!==id));

  /* Import merge by phone */
  const importLeads = incoming => {
    setLeads(prev=>{
      const map = {};
      prev.forEach(l=>{ if(l.phone) map[l.phone.replace(/\D/g,'')]=l; });
      const next = [...prev];
      incoming.forEach(il=>{
        const key = il.phone.replace(/\D/g,'');
        const idx = next.findIndex(l=>l.phone.replace(/\D/g,'')===key && key);
        if (idx>=0) next[idx]={...next[idx],...il,id:next[idx].id};
        else next.push(il);
      });
      return next;
    });
  };

  /* Convert to Client */
  const doConvert = lead => {
    const fullValue = lead.value || 0;
    const converted = {
      ...lead, convertedAt: new Date().toISOString(),
      payStatus: 'Unpaid', clientStatus: 'Active',
    };
    // Save to clients
    try {
      const existing = JSON.parse(localStorage.getItem('ryzenos_clients') || '[]');
      localStorage.setItem('ryzenos_clients', JSON.stringify([...existing, converted]));
    } catch {}
    // Create draft invoice with FULL deal value -- advance/balance entered manually in Finance
    try {
      const invKey = 'ryzenos_invoices';
      const existing = JSON.parse(localStorage.getItem(invKey) || '[]');
      const newInv = {
        id: 'INV-' + String(Date.now()).slice(-5),
        client: lead.name,
        company: lead.company || lead.businessType || '',
        amount: fullValue,
        status: 'draft',
        date: new Date().toISOString().split('T')[0],
        due: '',
        lead: 'Converted',
        source: lead.source || '',
        service: lead.service || '',
        advanceAmt: '',
        balanceAmt: '',
        advancePaid: false,
        balancePaid: false,
        note: 'Invoice auto-created on lead conversion — set advance/balance manually in Finance',
        auto: true,
      };
      localStorage.setItem(invKey, JSON.stringify([newInv, ...existing]));
    } catch {}
    // Push notification
    try {
      const n = JSON.parse(localStorage.getItem('ryzenos_notifs')||'[]');
      n.unshift({id:Date.now(),msg:lead.name+' converted to client. Invoice created for ₹'+fullValue.toLocaleString('en-IN')+' — set advance/balance in Finance',time:new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}),read:false});
      localStorage.setItem('ryzenos_notifs', JSON.stringify(n.slice(0,50)));
    } catch {}
    setLeads(prev => prev.filter(l => l.id !== lead.id));
    setConvertLead(null);
    setPanelLead(null);
  };

  const TopActions = (
    <div className="ld-top-actions">
      <div className="ld-search-wrap">
        <SearchIcon/>
        <input className="ld-search" placeholder="Search leads..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      <select className="ld-filter-sel" value={filterSrc} onChange={e=>setFilterSrc(e.target.value)}>
        {SOURCES.map(s=><option key={s}>{s}</option>)}
      </select>
      <div className="ld-view-toggle">
        <button className={`ld-vt-btn${view==='kanban'?' active':''}`} onClick={()=>setView('kanban')}><KanbanIcon/></button>
        <button className={`ld-vt-btn${view==='list'?' active':''}`} onClick={()=>setView('list')}><ListIcon/></button>
      </div>
      <button className="ld-import-btn" onClick={()=>setImportModal(true)}><UploadIcon/> Import</button>
      <button className="ld-add-btn" onClick={()=>setAddModal(true)}>+ Add Lead</button>
    </div>
  );

  return (
    <div className="ld-wrap" data-theme={theme}>
      <Sidebar/>
      <div className="ld-main">
        <Topbar
          title="Leads"
          subtitle={`${leads.length} leads · ${fmtVal(leads.reduce((s,l)=>s+l.value,0))} pipeline · ${leads.filter(l=>isOverdue(l.followUp)).length} overdue`}
          actions={TopActions}
        />

        {/* Strip */}
        <div className="ld-strip">
          {strip.map(c=>(
            <div key={c.id} className="ld-strip-item">
              <div className="ld-strip-dot" style={{background:c.color}}/>
              <span className="ld-strip-label">{c.label}</span>
              <span className="ld-strip-count">{c.count}</span>
              {c.total>0 && <span className="ld-strip-val">{fmtVal(c.total)}</span>}
            </div>
          ))}
        </div>

        {view==='kanban' ? (
          /* ── Kanban ── */
          <div className="ld-board">
            {COLUMNS.map(col=>(
              <div
                key={col.id}
                className="ld-col"
                onDragOver={e=>e.preventDefault()}
                onDrop={e=>onDrop(e,col.id)}
              >
                <div className="ld-col-hd">
                  <div className="ld-col-dot" style={{background:col.color}}/>
                  <span className="ld-col-label">{col.label}</span>
                  <span className="ld-col-count">{colLeads(col).length}</span>
                </div>
                <div className="ld-col-body">
                  {colLeads(col).length===0 && <div className="ld-col-empty">No leads</div>}
                  {colLeads(col).map(l=>(
                    <LeadCard key={l.id} lead={l} onDragStart={onDragStart} onClick={setPanelLead}/>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* ── List ── */
          <div className="ld-list-wrap">
            <table className="ld-table">
              <thead>
                <tr>{['Name','Source','Service','Budget','Stage','Pay Status','Follow-up','Assigned'].map(h=><th key={h} className="ld-th">{h}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map(l=>(
                  <tr key={l.id} className="ld-tr" onClick={()=>setPanelLead(l)}>
                    <td className="ld-td">
                      <div className="ld-tr-name-wrap">
                        <div className="ld-tr-av">{initials(l.name)}</div>
                        <div><div className="ld-tr-name">{l.name}</div><div className="ld-tr-email">{l.company||l.businessType||'—'}</div></div>
                      </div>
                    </td>
                    <td className="ld-td"><span className="ld-src-pill">{l.source}</span></td>
                    <td className="ld-td ld-td-muted">{l.service||'—'}</td>
                    <td className="ld-td ld-td-gold">{l.value?fmtVal(l.value):'—'}</td>
                    <td className="ld-td"><Badge status={l.status}/></td>
                    <td className="ld-td ld-td-muted">{l.payStatus||'—'}</td>
                    <td className={`ld-td ${isOverdue(l.followUp)?'ld-td-over':''}`}>{fmtDate(l.followUp)}</td>
                    <td className="ld-td ld-td-muted">{l.assigned}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addModal && <LeadModal theme={theme} onClose={()=>setAddModal(false)} onSave={saveLead}/>}
      {editLead && <LeadModal lead={editLead} theme={theme} onClose={()=>setEditLead(null)} onSave={l=>{saveLead(l);setEditLead(null);setPanelLead(l);}}/>}
      {panelLead && <LeadPanel lead={panelLead} theme={theme} onClose={()=>setPanelLead(null)} onEdit={l=>{setPanelLead(null);setEditLead(l);}} onDelete={id=>{deleteLead(id);}} onConvert={l=>setConvertLead(l)}/>}
      {importModal && <ImportModal theme={theme} onClose={()=>setImportModal(false)} onImport={importLeads}/>}
      {convertLead && <ConvertModal lead={convertLead} theme={theme} onConfirm={()=>doConvert(convertLead)} onClose={()=>setConvertLead(null)}/>}
    </div>
  );
}
